import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../config/axios';
import API_CONFIG from '../config/api';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  // Validation functions
  const validateName = (name) => {
    if (!name || name.length < 2 || name.length > 100) {
      return 'Name must be between 2 and 100 characters';
    }
    return null;
  };

  const validateEmail = (email) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email || !emailPattern.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const validatePassword = (password) => {
    if (!password || password.length < 8 || password.length > 128) {
      return 'Password must be between 8 and 128 characters';
    }
    
    const hasDigit = /\d/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    if (!hasDigit) {
      return 'Password must contain at least one digit';
    }
    if (!hasLowercase) {
      return 'Password must contain at least one lowercase character';
    }
    if (!hasUppercase) {
      return 'Password must contain at least one uppercase character';
    }
    if (!hasSpecial) {
      return 'Password must contain at least one special character';
    }
    
    return null;
  };

  const validatePhone = (phone) => {
    if (!phone) {
      return 'Phone number is required';
    }
    
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Check if it's a valid phone number (10-15 digits)
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      return 'Please enter a valid phone number (10-15 digits)';
    }
    
    return null;
  };

  const validateForm = () => {
    const newErrors = {};
    
    const nameError = validateName(formData.name);
    if (nameError) newErrors.name = nameError;
    
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;
    
    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;
    
    const phoneError = validatePhone(formData.phone);
    if (phoneError) newErrors.phone = phoneError;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear API error when user makes changes
    if (apiError) {
      setApiError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setApiError('');
    
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.REGISTER, {
        full_name: formData.name,
        email: formData.email,
        password: formData.password,
        phone_number: formData.phone
      });
      
      if (response.status === 200) {
        // Registration successful - user is now logged in via cookie
        // Send verification code
        try {
          await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.RESEND_VERIFICATION_CODE, {
            email: formData.email
          });
        } catch (verificationError) {
          console.error('Failed to send verification code:', verificationError);
          // Continue to verification page even if sending fails
        }
        
        // Redirect to verify email page with email
        navigate('/verify-email', { state: { email: formData.email } });
      }
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response) {
        // Server responded with error status
        setApiError(error.response.data?.message || 'An error occurred during registration. Please try again.');
      } else if (error.request) {
        // Request was made but no response received
        setApiError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        // Something else happened
        setApiError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="questionnaire-page">
      <div className="page-header">
        <div className="page-info">
          <h2>Create Account</h2>
          <p className="page-description">
            Join MEKA Deal Flow to access the 3X Assessment and unlock your full business potential
          </p>
        </div>
      </div>

      <div className="questions-container">
        <div className="question-block">
          <form onSubmit={handleSubmit}>
          {apiError && (
            <div className="error-message">
              {apiError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              className={errors.name ? 'error' : ''}
              required
            />
            {errors.name && (
              <div className="field-error">{errors.name}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email address"
              className={errors.email ? 'error' : ''}
              required
            />
            {errors.email && (
              <div className="field-error">{errors.email}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Enter your phone number"
              className={errors.phone ? 'error' : ''}
              required
            />
            {errors.phone && (
              <div className="field-error">{errors.phone}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Create a secure password"
              className={errors.password ? 'error' : ''}
              required
            />
            {errors.password && (
              <div className="field-error">{errors.password}</div>
            )}
            <div className="password-requirements">
              <p>Password requirements:</p>
              <ul>
                <li>8-128 characters long</li>
                <li>At least one digit (0-9)</li>
                <li>At least one lowercase letter (a-z)</li>
                <li>At least one uppercase letter (A-Z)</li>
                <li>At least one special character (!@#$%^&*...)</li>
              </ul>
            </div>
          </div>

          <button
            type="submit"
            className="submit-btn"
            disabled={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-links">
            <p>
              Already have an account?{' '}
              <button 
                type="button" 
                className="link-button"
                onClick={() => navigate('/login')}
              >
                Sign in here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
